/**
 * Self-check (Theme provider):
 * - Supplies semantic light/dark tokens through styled-components + GlobalStyle with persisted toggle.
 * - Verified body class & data-theme sync so legacy selectors and focus/hover states update immediately.
 * - Does not touch colors directly; consumers rely on theme tokens; no third-party overrides involved.
 */
import React, { createContext, useState, useEffect, useMemo } from "react";
import { ThemeProvider as StyledThemeProvider } from "styled-components";
import { lightTheme, darkTheme } from "../ui/theme";
import { GlobalStyle } from "../ui/GlobalStyle";

export const ThemeContext = createContext();

const getInitialMode = () => {
  if (typeof window === "undefined") {
    return "light";
  }

  // A choice the visitor made themselves always wins over the default.
  const stored = localStorage.getItem("theme");
  if (stored === "light" || stored === "dark") {
    return stored;
  }

  // Light is the primary theme: first-time visitors get it regardless of their
  // OS preference. Dark remains available through the toggle, and once toggled
  // the choice is persisted above.
  return "light";
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(getInitialMode);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    document.body.classList.remove("light", "dark");
    document.body.classList.add(theme);
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () =>
    setTheme((prev) => (prev === "light" ? "dark" : "light"));

  const resolvedTheme = useMemo(
    () => (theme === "dark" ? darkTheme : lightTheme),
    [theme]
  );

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <StyledThemeProvider theme={resolvedTheme}>
        <GlobalStyle />
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};
